import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { groupGuidesByCategory, getGuidePublicPath, GUIDES_INDEX_PATH } from '../../data/guideCategoryMeta';
import { loadGuidesListApiFirst } from '../../utils/cmsContentLoader';
import { Seo, Loading, ContentAdSlot } from '../shared';
import '../../styles/guides.scss';

const HUB_LINKS = [
	{ to: '/visa', label: 'Visa & arrival' },
	{ to: '/trekking', label: 'Trekking' },
	{ to: '/transportation', label: 'Transport' },
	{ to: '/safety', label: 'Safety & health' },
	{ to: '/festivals', label: 'Festivals' },
	{ to: '/temples', label: 'Temples' },
	{ to: '/shopping', label: 'Shopping' },
	{ to: '/journeys', label: 'Guided journeys' },
];

function GuidesIndexPage() {
	const [guides, setGuides] = useState([]);
	const [loading, setLoading] = useState(true);
	const [query, setQuery] = useState('');

	useEffect(() => {
		loadGuidesListApiFirst()
			.then(setGuides)
			.finally(() => setLoading(false));
	}, []);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return guides;
		return guides.filter(
			(g) => (g.title || '').toLowerCase().includes(q)
				|| (g.summary || '').toLowerCase().includes(q)
		);
	}, [guides, query]);

	const groups = useMemo(() => groupGuidesByCategory(filtered), [filtered]);

	return (
		<div className="hub-page guides-index-page">
			<Seo
				title="Nepal Travel Guide — Visa, Trekking, Safety & More"
				path={GUIDES_INDEX_PATH}
				description="Browse the Visit All Nepal travel guide — visa, permits, trekking, arrival, safety, temples, shopping, and practical trip planning."
			/>

			<section className="hub-hero media-frame media-frame--hero" style={{ background: 'linear-gradient(135deg, #1a2d4a 0%, #253f72 100%)' }}>
				<div className="container text-center hub-hero__content py-5">
					<h1 className="text-white mb-2">Nepal travel guide</h1>
					<p className="text-white hub-hero__tagline mx-auto">
						Every practical guide we publish — visa, permits, trekking, arrival, safety, and more.
						Pick a topic below or search by keyword.
					</p>
				</div>
			</section>

			<div className="container hub-page__body">
				<div className="guides-index-toolbar">
					<input
						type="search"
						className="form-control"
						placeholder="Search guides (visa, insurance, trekking…)"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						aria-label="Search guides"
					/>
					<div className="guides-index-hubs">
						<span className="text-muted small mr-2">Planning hubs:</span>
						{HUB_LINKS.map((h) => (
							<Link key={h.to} to={h.to} className="guides-index-hubs__link">{h.label}</Link>
						))}
					</div>
				</div>

				<ContentAdSlot layout="guides-index" className="adsense-unit--in-content my-4" />

				{loading && <Loading />}

				{!loading && groups.length === 0 && (
					<p className="text-muted text-center py-5">No guides match your search.</p>
				)}

				{!loading && groups.map((group) => (
					<section key={group.category} id={`category-${group.category}`} className="guides-index-group">
						<div className="guides-index-group__head">
							<h2 className="guides-index-group__title">{group.label}</h2>
							{group.hub && (
								<Link to={group.hub} className="guides-index-group__hub-link">
									{group.label} hub →
								</Link>
							)}
						</div>
						<div className="guide-grid">
							{group.guides.map((g) => (
								<Link key={g.slug} to={getGuidePublicPath(g.slug)} className="guide-card">
									<span className="guide-card__category">{group.label}</span>
									<h3 className="guide-card__title">{g.title}</h3>
									{g.summary && <p className="guide-card__summary">{g.summary}</p>}
								</Link>
							))}
						</div>
					</section>
				))}

				{!loading && guides.length > 0 && (
					<p className="text-muted small text-center mt-4 mb-0">
						{guides.length} guides · Also explore{' '}
						<Link to="/journeys">step-by-step trek journeys</Link>
					</p>
				)}
			</div>
		</div>
	);
}

export default GuidesIndexPage;
